import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useTheme } from '@mui/material'
import { ElectricBolt } from '@mui/icons-material'
import { AuroraAssetGroup } from '../types'
import { AuroraHeatingElementMode } from '../enums'
import AuroraHeatingElementRelayModeControl from './AuroraHeatingElementRelayModeControl'
import AuroraHeatingElementOffsetTemperatureControl from './AuroraHeatingElementOffsetTemperatureControl'
import AuroraControlGroup from './AuroraControlGroup'
import { useAuroraStack } from '../hooks/aurora'
import {
  setAuroraHeatingElement1RelayMode,
  setAuroraHeatingElement2RelayMode,
  setAuroraHeatingElement1OffsetTemperature,
  setAuroraHeatingElement2OffsetTemperature
} from '../services/aurora'
import { createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupHeatingElementControlsProps {
  auroraAssetGroup: AuroraAssetGroup
  element: 1 | 2
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupHeatingElementControls({
  auroraAssetGroup,
  element,
  onError
}: AuroraAssetGroupHeatingElementControlsProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const theme = useTheme()

  const { awhpAuroras, awhpMaster, handleAuroraAwhpActionError } = useAuroraStack(auroraAssetGroup)

  const initialRelayMode = useMemo(() => {
    if (awhpMaster !== null) {
      return (
        (element === 1
          ? awhpMaster.heatingElement1RelayMode
          : awhpMaster.heatingElement2RelayMode) ?? AuroraHeatingElementMode.OFF
      )
    }

    if (awhpAuroras.length === 0) {
      return AuroraHeatingElementMode.OFF
    }

    const [firstMode, ...restModes] = awhpAuroras.map((aurora) =>
      element === 1 ? aurora.heatingElement1RelayMode : aurora.heatingElement2RelayMode
    )

    const everyModeMatches = restModes.every((mode) => mode === firstMode)

    return (everyModeMatches ? firstMode : null) ?? AuroraHeatingElementMode.OFF
  }, [awhpMaster, awhpAuroras, element])

  const initialOffsetTemperature = useMemo(() => {
    if (awhpMaster !== null) {
      return element === 1
        ? awhpMaster.heatingElement1OffsetTemperature
        : awhpMaster.heatingElement2OffsetTemperature
    }

    if (awhpAuroras.length === 0) {
      return null
    }

    const [firstValue, ...restValues] = awhpAuroras.map((aurora) =>
      element === 1
        ? aurora.heatingElement1OffsetTemperature
        : aurora.heatingElement2OffsetTemperature
    )

    return restValues.every((value) => value === firstValue) ? firstValue : null
  }, [awhpMaster, awhpAuroras, element])

  const [relayMode, setRelayMode] = useState<AuroraHeatingElementMode>(initialRelayMode)

  const [offsetTemperature, setOffsetTemperature] = useState<number | null>(
    initialOffsetTemperature
  )

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  async function handleSetRelayMode(mode: AuroraHeatingElementMode) {
    try {
      setSubmitting(true)

      await Promise.all(
        awhpAuroras.map((aurora) =>
          element === 1
            ? setAuroraHeatingElement1RelayMode(aurora.id, mode)
            : setAuroraHeatingElement2RelayMode(aurora.id, mode)
        )
      )

      dispatch(
        createSuccessNotification(
          t(
            element === 1
              ? 'setHeatingElement1RelayModeSuccess'
              : 'setHeatingElement2RelayModeSuccess'
          )
        )
      )
      setRelayMode(mode)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(
        error,
        t(element === 1 ? 'setHeatingElement1RelayModeError' : 'setHeatingElement2RelayModeError')
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSetOffsetTemperature(value: number) {
    try {
      setSubmitting(true)

      await Promise.all(
        awhpAuroras.map((aurora) =>
          element === 1
            ? setAuroraHeatingElement1OffsetTemperature(aurora.id, value)
            : setAuroraHeatingElement2OffsetTemperature(aurora.id, value)
        )
      )

      dispatch(
        createSuccessNotification(
          t(
            element === 1
              ? 'setHeatingElement1OffsetTemperatureSuccess'
              : 'setHeatingElement2OffsetTemperatureSuccess'
          )
        )
      )
      setOffsetTemperature(value)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(
        error,
        t(
          element === 1
            ? 'setHeatingElement1OffsetTemperatureError'
            : 'setHeatingElement2OffsetTemperatureError'
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (awhpAuroras.length === 0) {
    return null
  }

  return (
    <AuroraControlGroup
      label={t(element === 1 ? 'heatingElement1' : 'heatingElement2')}
      icon={<ElectricBolt htmlColor={theme.palette.text.secondary} />}
    >
      <AuroraHeatingElementRelayModeControl
        label={t(element === 1 ? 'heatingElement1RelayMode' : 'heatingElement2RelayMode')}
        value={relayMode}
        loading={isSubmitting}
        onSubmit={handleSetRelayMode}
      />

      <AuroraHeatingElementOffsetTemperatureControl
        label={t(
          element === 1 ? 'heatingElement1OffsetTemperature' : 'heatingElement2OffsetTemperature'
        )}
        value={offsetTemperature}
        loading={isSubmitting}
        onSubmit={handleSetOffsetTemperature}
      />
    </AuroraControlGroup>
  )
}
